/**
 * Layout — app shell: AppHeader (top) + single left AppSidebar + full content.
 * Hosts the global Command Palette (⌘K) and tracks Favorites / Recently
 * Visited via useNavPrefs.
 */
import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import AppHeader from './layout/AppHeader';
import AppSidebar from './layout/AppSidebar';
import CommandPalette from './layout/CommandPalette';
import Breadcrumbs from './layout/Breadcrumbs';
import CopilotLauncher from './copilot/CopilotLauncher';
import { resolveModuleId, findPage } from './layout/modulesConfig';
import { useNavPrefs } from '../hooks/useNavPrefs';

const read = (key, fallback) => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
};

const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const prefs = useNavPrefs();

  const activeModuleId = resolveModuleId(location.pathname);
  const [commandOpen, setCommandOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(() => read('sidebarOpen', true));

  useEffect(() => {
    localStorage.setItem('sidebarOpen', JSON.stringify(sidebarOpen));
  }, [sidebarOpen]);

  // Track recently-visited pages.
  useEffect(() => {
    const page = findPage(location.pathname);
    if (page) prefs.pushRecent(page);
  // eslint-disable-next-line
  }, [location.pathname]);

  // Global Cmd/Ctrl+K to toggle the command palette.
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setCommandOpen((o) => !o);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  return (
    <div className="min-h-screen bg-gray-50" data-testid="erp-layout">
      <AppHeader
        user={user}
        logout={logout}
        onOpenCommand={() => setCommandOpen(true)}
        onToggleSidebar={() => setSidebarOpen((o) => !o)}
      />

      <div className="flex">
        {sidebarOpen && <AppSidebar prefs={prefs} activeModuleId={activeModuleId} />}

        <main className="flex-1 min-w-0" data-testid="main-content">
          <Breadcrumbs />
          <div key={location.pathname} className="page-transition">
            {children}
          </div>
        </main>
      </div>

      <CommandPalette open={commandOpen} onOpenChange={setCommandOpen} prefs={prefs} />
      <CopilotLauncher />
    </div>
  );
};

export default Layout;
